///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2024
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import { includes } from "lodash";

import messages from "../../../helpers/messages";
import ChevronRight from "../../icons/ChevronRight";
import {
  clearRowsSelected,
  fetchCarrierMatches,
  saveCarrierMatches,
  setCapacityMatchCarriersInfo,
  setCopyMatchesFromRow,
  setRowsSelected,
  setSaveCarrierMatchDetails,
  updateStep,
  fetchRateHistory
} from "../../../slices/bidProcurementSlice";
import MenuDropdown from "./MenuDropdown";
import Tooltip from "../../Tooltip";
import ClearSelectedMatchesModal from "./ClearSelectedMatchesModal";
import SystemErrorModal from "../../SystemErrorModal";
import reverseMapLaneCarrierCapacityMatch from "../../../helpers/reverseMapLaneCarrierCapacityMatch";
import capacityMatchLaneSummaryColumns from "../../../helpers/capacityMatchLaneSummaryColumns";
import AlertIcon from "../../icons/AlertIcon";
import {
  mode as modeType,
  capacityMatchingStatus as capacityMatchingStatusType
} from "../../../helpers/types";

const initialDetails = { open: false, laneId: "" };

const CapacityMatchTableBody = ({
  isPendingOrRejected,
  bidId,
  setOpenDrawer,
  isSubmitted,
  isReviewForCarrierReps,
  isCapacityMatchingInProgress
}) => {
  const dispatch = useDispatch();
  const {
    bidProcurement: {
      capacityMatchLaneSummary: {
        capacityMatches,
        offset,
        perPage,
        columns: { order: columnOrder }
      },
      rowsSelected,
      copyMatchesFromRow,
      saveCarrierMatchDetails,
      matchesLastRanBefore7Days
    },
    bidSummary: { summary },
    featureFlags: { enableDragAndDropForIcm, enableExportMatches }
  } = useSelector(state => state);
  const [openClearSelectedMatchesModal, setOpenClearSelectedMatchesModal] =
    useState(initialDetails);

  const capacityMatchingStatus = summary?.modes.find(
    ({ mode }) => mode === modeType.tl
  )?.capacityMatchingStatus;

  const isInProgressOrReview =
    capacityMatchingStatus === capacityMatchingStatusType.inProgress ||
    capacityMatchingStatus === capacityMatchingStatusType.review;

  const BodyCell = ({ className, value, children, maxWidth, ...rest }) => (
    <td
      className={"body-cell py-12 px-8 align-middle " + className}
      style={{ maxWidth: maxWidth }}
      {...rest}
    >
      {value || children}
    </td>
  );

  const clearSelectedMatches = () => {
    dispatch(
      saveCarrierMatches(
        bidId,
        false,
        () => {
          setOpenClearSelectedMatchesModal(initialDetails);
        },
        true
      )
    );
  };

  const closeErrorModal = () =>
    dispatch(setSaveCarrierMatchDetails({ isFetching: false, isError: false }));

  const showWarning = () => {
    return (
      <Tooltip content={messages.exportMatchesWarningMessage.defaultMessage}>
        <span>
          <AlertIcon
            height={14}
            width={16}
            className="ml-8"
            data-testid="alert-icon"
          />
        </span>
      </Tooltip>
    );
  };

  const matchesToRender = capacityMatches
    .filter(match => match.metaData.matched)
    .slice(offset, offset + perPage);

  return (
    <>
      <tbody data-testid="capacity-matches-table-body">
        {matchesToRender.map((capacityMatch, index) => {
          let carrierNames = capacityMatch.laneMatches?.map(laneMatch =>
            laneMatch.carrierId === null
              ? messages.none.defaultMessage
              : `${laneMatch.carrierName} (${laneMatch.carrierCode})`
          );
          if (isSubmitted && carrierNames?.length === 0) {
            carrierNames = [messages.none.defaultMessage];
          }

          return (
            <tr className="row bg-white" key={index}>
              {enableDragAndDropForIcm ? (
                columnOrder.map((co, index) => {
                  const column = capacityMatchLaneSummaryColumns.find(
                    c => c.fieldName === co
                  );

                  return column.fieldName === "truckloadLaneId" ? (
                    <BodyCell maxWidth={50} key={`${co}_${index}`}>
                      <input
                        type="checkbox"
                        onChange={() =>
                          dispatch(
                            setRowsSelected(capacityMatch.truckloadLaneId)
                          )
                        }
                        checked={
                          rowsSelected.some(
                            x => x === capacityMatch.truckloadLaneId
                          ) ||
                          copyMatchesFromRow === capacityMatch.truckloadLaneId
                        }
                        disabled={
                          isPendingOrRejected ||
                          isSubmitted ||
                          isReviewForCarrierReps ||
                          isCapacityMatchingInProgress ||
                          copyMatchesFromRow === capacityMatch.truckloadLaneId
                        }
                      />
                    </BodyCell>
                  ) : column.fieldName === "groups" ? (
                    isPendingOrRejected ? (
                      <BodyCell
                        value={"---"}
                        maxWidth={90}
                        key={`${co}_${index}`}
                      />
                    ) : (
                      <BodyCell
                        value={capacityMatch.groups?.join(", ")}
                        maxWidth={150}
                        key={`${co}_${index}`}
                      />
                    )
                  ) : column.fieldName === "procurementRepNames" ? (
                    isPendingOrRejected ? (
                      <BodyCell
                        value={"---"}
                        maxWidth={90}
                        key={`${co}_${index}`}
                      />
                    ) : (
                      <BodyCell maxWidth={200} key={`${co}_${index}`}>
                        <Tooltip
                          placement="top-start"
                          maxWidth={180}
                          content={capacityMatch.procurementRepNames?.join(", ")}
                        >
                          <span>{capacityMatch.procurementRepNames?.join(", ")}</span>
                        </Tooltip>
                      </BodyCell>
                    )
                  ) : column.fieldName === "carrierNames" ? (
                    isPendingOrRejected ? (
                      <BodyCell
                        value={"---"}
                        maxWidth={90}
                        key={`${co}_${index}`}
                      />
                    ) : (
                      <BodyCell maxWidth={200} key={`${co}_${index}`}>
                        <Tooltip
                          placement="top-start"
                          maxWidth={180}
                          content={capacityMatch.carrierNames.join(", ")}
                        >
                          <span>{capacityMatch.carrierNames.join(", ")}</span>
                        </Tooltip>
                      </BodyCell>
                    )
                  ) : column.fieldName === "repNames" ? (
                    isPendingOrRejected ? (
                      <BodyCell
                        value={"---"}
                        maxWidth={90}
                        key={`${co}_${index}`}
                      />
                    ) : (
                      <BodyCell maxWidth={200} key={`${co}_${index}`}>
                        <Tooltip
                          placement="top-start"
                          maxWidth={180}
                          content={capacityMatch.repNames?.join(", ")}
                        >
                          <span>{capacityMatch.repNames?.join(", ")}</span>
                        </Tooltip>
                      </BodyCell>
                    )
                  ) : column.fieldName === "incumbentMatchesCount" ? (
                    isPendingOrRejected ? (
                      <BodyCell maxWidth={100} key={`${co}_${index}`}>
                        <span className="carriers-cell zero-carriers-cell">
                          <FormattedMessage
                            {...messages.carrierCount}
                            values={{ noOfCarriers: 0 }}
                          />
                        </span>
                      </BodyCell>
                    ) : (
                      <BodyCell maxWidth={100} key={`${co}_${index}`}>
                        <span
                          className={`carriers-cell ${
                            capacityMatch.incumbentMatchesCount === 0
                              ? "no-carriers-cell"
                              : ""
                          }`}
                        >
                          {capacityMatch.incumbentMatchesCount ? (
                            <FormattedMessage
                              {...messages.carrierCount}
                              values={{
                                noOfCarriers:
                                  capacityMatch.incumbentMatchesCount
                              }}
                            />
                          ) : (
                            messages.none.defaultMessage
                          )}
                        </span>
                      </BodyCell>
                    )
                  ) : column.fieldName === "matches" ? (
                    isPendingOrRejected ? (
                      <BodyCell maxWidth={350} key={`${co}_${index}`}>
                        <span className="match-cell zero-match-cell m-0 p-0">
                          0
                        </span>
                        <button className="view-button ml-20" disabled={true}>
                          {messages.view.defaultMessage}
                          <ChevronRight
                            className="align-text-bottom ml-8"
                            fill="#53a6d8"
                          />
                        </button>
                        {enableExportMatches &&
                          isInProgressOrReview &&
                          matchesLastRanBefore7Days &&
                          showWarning()}
                      </BodyCell>
                    ) : (
                      <BodyCell maxWidth={350} key={`${co}_${index}`}>
                        {carrierNames?.length > 0 ? (
                          <>
                            {!includes(carrierNames, "None") ? (
                              <Tooltip
                                placement="top-start"
                                content={
                                  <ul className="mb-0">
                                    {carrierNames.map((c, index) => (
                                      <li className="p-4" key={`${c}-${index}`}>
                                        {c}
                                      </li>
                                    ))}
                                  </ul>
                                }
                              >
                                <span
                                  className="inline-block truncate"
                                  style={{ maxWidth: 270 }}
                                >
                                  {carrierNames.join(", ")}
                                </span>
                              </Tooltip>
                            ) : (
                              <span
                                className="inline-block truncate"
                                style={{ maxWidth: 270 }}
                              >
                                {carrierNames.join(", ")}
                              </span>
                            )}
                            {enableExportMatches &&
                              isInProgressOrReview &&
                              matchesLastRanBefore7Days &&
                              showWarning()}
                            <MenuDropdown
                              disabled={
                                isSubmitted ||
                                isReviewForCarrierReps ||
                                isCapacityMatchingInProgress
                              }
                              clearSelectedMatches={() => {
                                dispatch(setCopyMatchesFromRow(null));
                                dispatch(clearRowsSelected());
                                dispatch(
                                  setRowsSelected(capacityMatch.truckloadLaneId)
                                );
                                setOpenClearSelectedMatchesModal({
                                  open: true,
                                  laneId: capacityMatch.shipperLaneId
                                });
                              }}
                              editMatches={() => {
                                dispatch(setCopyMatchesFromRow(null));
                                dispatch(clearRowsSelected());
                                dispatch(
                                  setRowsSelected(capacityMatch.truckloadLaneId)
                                );
                                setOpenDrawer(true);
                                dispatch(
                                  fetchCarrierMatches(bidId, () => {
                                    const {
                                      laneMatches,
                                      isNoneOptionSelected
                                    } = reverseMapLaneCarrierCapacityMatch(
                                      capacityMatches,
                                      [capacityMatch.truckloadLaneId]
                                    );
                                    dispatch(
                                      setCapacityMatchCarriersInfo({
                                        laneMatches,
                                        isNoneOptionSelected
                                      })
                                    );
                                    !isNoneOptionSelected &&
                                      dispatch(updateStep(2));
                                  })
                                );
                                dispatch(fetchRateHistory(bidId, capacityMatch.truckloadLaneId));
                              }}
                              copyMatches={() => {
                                dispatch(clearRowsSelected());
                                dispatch(
                                  setCopyMatchesFromRow(
                                    capacityMatch.truckloadLaneId
                                  )
                                );
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <span className="match-cell m-0 p-0">
                              {capacityMatch.matchCount}
                            </span>
                            <button
                              id="view-matches"
                              className="view-button ml-20"
                              onClick={() => {
                                dispatch(setCopyMatchesFromRow(null));
                                dispatch(clearRowsSelected());
                                dispatch(
                                  setRowsSelected(capacityMatch.truckloadLaneId)
                                );
                                setOpenDrawer(true);
                                dispatch(fetchCarrierMatches(bidId));
                                dispatch(fetchRateHistory(bidId, capacityMatch.truckloadLaneId));
                              }}
                              disabled={
                                isReviewForCarrierReps ||
                                isCapacityMatchingInProgress
                              }
                            >
                              {messages.view.defaultMessage}
                              <ChevronRight
                                className="align-text-bottom ml-8"
                                fill={`${
                                  isReviewForCarrierReps ? "#53a6d8" : "#0066FA"
                                }`}
                              />
                            </button>
                            {enableExportMatches &&
                              isInProgressOrReview &&
                              matchesLastRanBefore7Days &&
                              showWarning()}
                          </>
                        )}
                      </BodyCell>
                    )
                  ) : (
                    <BodyCell
                      value={capacityMatch[column.fieldName]}
                      maxWidth={column.minWidth}
                      className={column.right ? "text-right" : ""}
                      key={`${co}_${index}`}
                    />
                  );
                })
              ) : (
                <>
                  <BodyCell maxWidth={50}>
                    <input
                      type="checkbox"
                      onChange={() =>
                        dispatch(setRowsSelected(capacityMatch.truckloadLaneId))
                      }
                      checked={
                        rowsSelected.some(
                          x => x === capacityMatch.truckloadLaneId
                        ) ||
                        copyMatchesFromRow === capacityMatch.truckloadLaneId
                      }
                      disabled={
                        isPendingOrRejected ||
                        isSubmitted ||
                        isReviewForCarrierReps ||
                        isCapacityMatchingInProgress ||
                        copyMatchesFromRow === capacityMatch.truckloadLaneId
                      }
                    />
                  </BodyCell>
                  <BodyCell
                    value={capacityMatch.shipperLaneId}
                    maxWidth={105}
                  />
                  <BodyCell value={capacityMatch.originCity} maxWidth={110} />
                  <BodyCell value={capacityMatch.originState} maxWidth={100} />
                  <BodyCell
                    value={capacityMatch.originPostalCode}
                    maxWidth={125}
                  />
                  <BodyCell
                    value={capacityMatch.originCountry}
                    maxWidth={135}
                  />
                  <BodyCell
                    value={capacityMatch.destinationCity}
                    maxWidth={115}
                  />
                  <BodyCell
                    value={capacityMatch.destinationState}
                    maxWidth={100}
                  />
                  <BodyCell
                    value={capacityMatch.destinationPostalCode}
                    maxWidth={125}
                  />
                  <BodyCell
                    value={capacityMatch.destinationCountry}
                    maxWidth={135}
                  />
                  <BodyCell value={capacityMatch.equipmentType} maxWidth={90} />
                  <BodyCell
                    value={capacityMatch.volume}
                    className="text-right"
                    maxWidth={110}
                  />

                  {isPendingOrRejected ? (
                    <BodyCell value={"---"} maxWidth={90} />
                  ) : (
                    <BodyCell
                      value={capacityMatch.groups?.join(", ")}
                      maxWidth={150}
                    />
                  )}
                  
                  {isPendingOrRejected ? (
                    <BodyCell value={"---"} maxWidth={90} />
                  ) : (
                    <BodyCell maxWidth={200}>
                      <Tooltip
                        placement="top-start"
                        maxWidth={180}
                        content={capacityMatch.procurementRepNames?.join(", ")}
                      >
                        <span>{capacityMatch.procurementRepNames?.join(", ")}</span>
                      </Tooltip>
                    </BodyCell>
                  )}

                  {isPendingOrRejected ? (
                    <BodyCell value={"---"} maxWidth={90} />
                  ) : (
                    <BodyCell maxWidth={200}>
                      <Tooltip
                        placement="top-start"
                        maxWidth={180}
                        content={capacityMatch.carrierNames.join(", ")}
                      >
                        <span>{capacityMatch.carrierNames.join(", ")}</span>
                      </Tooltip>
                    </BodyCell>
                  )}

                  {isPendingOrRejected ? (
                    <BodyCell value={"---"} maxWidth={90} />
                  ) : (
                    <BodyCell maxWidth={200}>
                      <Tooltip
                        placement="top-start"
                        maxWidth={180}
                        content={capacityMatch.repNames?.join(", ")}
                      >
                        <span>{capacityMatch.repNames?.join(", ")}</span>
                      </Tooltip>
                    </BodyCell>
                  )}

                  {isPendingOrRejected ? (
                    <BodyCell maxWidth={100}>
                      <span className="carriers-cell zero-carriers-cell">
                        <FormattedMessage
                          {...messages.carrierCount}
                          values={{ noOfCarriers: 0 }}
                        />
                      </span>
                    </BodyCell>
                  ) : (
                    <BodyCell maxWidth={100}>
                      <span
                        className={`carriers-cell ${
                          capacityMatch.incumbentMatchesCount === 0
                            ? "no-carriers-cell"
                            : ""
                        }`}
                      >
                        {capacityMatch.incumbentMatchesCount ? (
                          <FormattedMessage
                            {...messages.carrierCount}
                            values={{
                              noOfCarriers: capacityMatch.incumbentMatchesCount
                            }}
                          />
                        ) : (
                          messages.none.defaultMessage
                        )}
                      </span>
                    </BodyCell>
                  )}

                  {isPendingOrRejected ? (
                    <BodyCell maxWidth={350}>
                      <span className="match-cell zero-match-cell m-0 p-0">
                        0
                      </span>
                      <button className="view-button ml-20" disabled={true}>
                        {messages.view.defaultMessage}
                        <ChevronRight
                          className="align-text-bottom ml-8"
                          fill="#53a6d8"
                        />
                        {enableExportMatches &&
                          isInProgressOrReview &&
                          matchesLastRanBefore7Days &&
                          showWarning()}
                      </button>
                    </BodyCell>
                  ) : (
                    <BodyCell maxWidth={350}>
                      {carrierNames?.length > 0 ? (
                        <>
                          {!includes(carrierNames, "None") ? (
                            <Tooltip
                              placement="top-start"
                              content={
                                <ul className="mb-0">
                                  {carrierNames.map((c, index) => (
                                    <li className="p-4" key={`${c}-${index}`}>
                                      {c}
                                    </li>
                                  ))}
                                </ul>
                              }
                            >
                              <span
                                className="inline-block truncate"
                                style={{ maxWidth: 270 }}
                              >
                                {carrierNames.join(", ")}
                              </span>
                            </Tooltip>
                          ) : (
                            <span
                              className="inline-block truncate"
                              style={{ maxWidth: 270 }}
                            >
                              {carrierNames.join(", ")}
                            </span>
                          )}
                          {enableExportMatches &&
                            isInProgressOrReview &&
                            matchesLastRanBefore7Days &&
                            showWarning()}
                          <MenuDropdown
                            disabled={
                              isSubmitted ||
                              isReviewForCarrierReps ||
                              isCapacityMatchingInProgress
                            }
                            clearSelectedMatches={() => {
                              dispatch(setCopyMatchesFromRow(null));
                              dispatch(clearRowsSelected());
                              dispatch(
                                setRowsSelected(capacityMatch.truckloadLaneId)
                              );
                              setOpenClearSelectedMatchesModal({
                                open: true,
                                laneId: capacityMatch.shipperLaneId
                              });
                            }}
                            editMatches={() => {
                              dispatch(setCopyMatchesFromRow(null));
                              dispatch(clearRowsSelected());
                              dispatch(
                                setRowsSelected(capacityMatch.truckloadLaneId)
                              );
                              setOpenDrawer(true);
                              dispatch(
                                fetchCarrierMatches(bidId, () => {
                                  const { laneMatches, isNoneOptionSelected } =
                                    reverseMapLaneCarrierCapacityMatch(
                                      capacityMatches,
                                      [capacityMatch.truckloadLaneId]
                                    );
                                  dispatch(
                                    setCapacityMatchCarriersInfo({
                                      laneMatches,
                                      isNoneOptionSelected
                                    })
                                  );
                                  !isNoneOptionSelected &&
                                    dispatch(updateStep(2));
                                })
                              );
                              dispatch(fetchRateHistory(bidId, capacityMatch.truckloadLaneId));
                            }}
                            copyMatches={() => {
                              dispatch(clearRowsSelected());
                              dispatch(
                                setCopyMatchesFromRow(
                                  capacityMatch.truckloadLaneId
                                )
                              );
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <span className="match-cell m-0 p-0">
                            {capacityMatch.matchCount}
                          </span>
                          <button
                            id="view-matches"
                            className="view-button ml-20"
                            onClick={() => {
                              dispatch(setCopyMatchesFromRow(null));
                              dispatch(clearRowsSelected());
                              dispatch(
                                setRowsSelected(capacityMatch.truckloadLaneId)
                              );
                              setOpenDrawer(true);
                              dispatch(fetchCarrierMatches(bidId));
                              dispatch(fetchRateHistory(bidId, capacityMatch.truckloadLaneId));
                            }}
                            disabled={
                              isReviewForCarrierReps ||
                              isCapacityMatchingInProgress
                            }
                          >
                            {messages.view.defaultMessage}
                            <ChevronRight
                              className="align-text-bottom ml-8"
                              fill={`${
                                isReviewForCarrierReps ? "#53a6d8" : "#0066FA"
                              }`}
                            />
                          </button>
                          {enableExportMatches &&
                            isInProgressOrReview &&
                            matchesLastRanBefore7Days &&
                            showWarning()}
                        </>
                      )}
                    </BodyCell>
                  )}
                </>
              )}
            </tr>
          );
        })}
      </tbody>
      <ClearSelectedMatchesModal
        {...openClearSelectedMatchesModal}
        open={
          openClearSelectedMatchesModal.open && !saveCarrierMatchDetails.isError
        }
        isFetching={saveCarrierMatchDetails.isFetching}
        handleClose={() => setOpenClearSelectedMatchesModal(initialDetails)}
        handleClearAndContinue={clearSelectedMatches}
      />
      <SystemErrorModal
        open={
          openClearSelectedMatchesModal.open && saveCarrierMatchDetails.isError
        }
        large
        closeModal={closeErrorModal}
        handleExit={closeErrorModal}
        handleTryAgain={clearSelectedMatches}
      />
    </>
  );
};

export default CapacityMatchTableBody;
